import { useState, type FC } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const ContactForm: FC = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const phoneAltInput = form.querySelector('input[name="phone_alt"]') as HTMLInputElement;
    if (phoneAltInput && phoneAltInput.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9n1c6m8mbnljin59sp0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(String(serverMsg));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      className="space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground-200 mb-1.5">
            姓名 <span className="text-primary-400">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            className="w-full px-4 py-2.5 bg-background-50 border border-background-300/40 rounded-lg text-sm text-foreground-100 placeholder-foreground-500 outline-none focus:border-primary-500/50 transition-colors"
            placeholder="请输入您的姓名"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground-200 mb-1.5">
            邮箱 <span className="text-primary-400">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            className="w-full px-4 py-2.5 bg-background-50 border border-background-300/40 rounded-lg text-sm text-foreground-100 placeholder-foreground-500 outline-none focus:border-primary-500/50 transition-colors"
            placeholder="请输入您的邮箱"
          />
        </div>
      </div>

      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-foreground-200 mb-1.5">
          主题 <span className="text-primary-400">*</span>
        </label>
        <input
          type="text"
          id="subject"
          name="subject"
          required
          className="w-full px-4 py-2.5 bg-background-50 border border-background-300/40 rounded-lg text-sm text-foreground-100 placeholder-foreground-500 outline-none focus:border-primary-500/50 transition-colors"
          placeholder="请输入主题"
        />
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-foreground-200 mb-1.5">
          留言内容 <span className="text-primary-400">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={5}
          maxLength={500}
          className="w-full px-4 py-2.5 bg-background-50 border border-background-300/40 rounded-lg text-sm text-foreground-100 placeholder-foreground-500 outline-none focus:border-primary-500/50 transition-colors resize-none"
          placeholder="请输入您的留言内容（不超过500字）"
        ></textarea>
      </div>

      <input
        type="text"
        name="phone_alt"
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        readOnly
        className="antibot-field"
      />

      <div>
        <button
          type="submit"
          disabled={formStatus === 'submitting'}
          className="whitespace-nowrap w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 font-medium rounded-lg transition-all duration-200 cursor-pointer text-sm"
        >
          {formStatus === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              提交中...
            </>
          ) : (
            <>
              <i className="ri-send-plane-line mr-2"></i>
              发送留言
            </>
          )}
        </button>
      </div>

      {formStatus === 'success' && (
        <div className="p-4 rounded-lg bg-accent-500/10 border border-accent-500/20">
          <p className="text-sm text-accent-400 flex items-center gap-2">
            <i className="ri-checkbox-circle-line"></i>
            感谢您的留言！我们会尽快回复您。
          </p>
        </div>
      )}

      {formStatus === 'error' && (
        <div className="p-4 rounded-lg bg-primary-500/10 border border-primary-500/20">
          <p className="text-sm text-primary-400 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {formError}
          </p>
        </div>
      )}
    </form>
  );
};

const faqItems = [
  {
    q: '棒棒TV的视频需要付费吗？',
    a: '不需要！棒棒TV的所有视频内容完全免费，无需注册会员，无需付费订阅，打开即可观看。我们致力于为用户提供零门槛的高清视频观看体验。',
  },
  {
    q: '观看视频需要下载客户端或插件吗？',
    a: '完全不需要。棒棒TV基于Web技术，直接在浏览器中播放。无论是电脑、手机还是平板，打开网站就能看，不卡顿、免缓冲，无需安装任何软件。',
  },
  {
    q: '棒棒TV支持哪些视频清晰度？',
    a: '棒棒TV支持从标清到4K超清多种清晰度，系统会根据您的网络状况智能选择最佳画质。您也可以手动切换清晰度，满足不同网络环境下的观影需求。',
  },
  {
    q: '视频更新速度怎么样？',
    a: '棒棒TV拥有专业的资源更新团队，热门剧集和电影会第一时间同步更新。电视剧通常在播出后数小时内即可观看，电影上线速度也处于行业领先水平。',
  },
  {
    q: '如何搜索我想看的视频？',
    a: '您可以通过顶部导航栏的分类浏览，或使用搜索框直接搜索您想看的视频名称。网站支持电视剧、电影、动漫、综艺、纪录片等多个品类的精准搜索。',
  },
];

export default function AboutPage() {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '棒棒TV',
    url: 'https://www.bangbangtv.com/',
    description: '棒棒TV提供免费在线观看高清视频，不卡顿、免缓冲、流畅播放。电视剧全集、电影大全、最新剧集一网打尽。',
    email: 'contact@bangbangtv.com',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '北京',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 棒棒TV品牌介绍与联系方式"
        description="了解棒棒TV的品牌故事与发展历程。棒棒TV是领先的免费在线高清视频平台，提供电视剧全集、电影大全、最新剧集等海量资源。不卡顿免缓冲流畅播放。"
        keywords="棒棒TV,关于我们,品牌介绍,联系方式,免费视频平台,在线观看"
        canonicalUrl="/about"
        schemaJson={{
          '@context': 'https://schema.org',
          '@graph': [orgSchema, faqSchema],
        }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Brand Intro Hero */}
            <section className="relative rounded-2xl overflow-hidden mb-14 aspect-[21/9] min-h-[280px] flex items-center">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20dark%20theater%20interior%20with%20red%20velvet%20curtains%20and%20spotlights%2C%20professional%20cinema%20atmosphere%2C%20empty%20stage%20with%20dramatic%20lighting%2C%20elegant%20entertainment%20venue%2C%20warm%20accent%20lighting%20on%20dark%20background&width=1600&height=600&seq=about-hero-01&orientation=landscape"
                alt="棒棒TV品牌故事"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background-950/85 to-background-950/40"></div>
              <div className="relative z-10 px-8 md:px-12 py-8 max-w-xl">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 mb-4">
                  关于 <span className="text-primary-400">棒棒TV</span>
                </h1>
                <p className="text-foreground-300 text-sm md:text-base leading-relaxed">
                  棒棒TV成立于2020年，是国内领先的免费在线高清视频平台。
                  我们以"让每个人都能免费看好视频"为使命，持续为用户提供海量优质的影视内容。
                </p>
              </div>
            </section>

            {/* Brand Story */}
            <section className="mb-14">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl font-heading font-semibold text-foreground-50 mb-4">品牌故事</h2>
                  <div className="space-y-4 text-foreground-300 leading-relaxed text-sm">
                    <p>
                      棒棒TV诞生于在线视频行业蓬勃发展的时代。我们的创始团队发现，尽管市场上视频平台众多，
                      但用户仍然面临着付费墙、广告轰炸、播放卡顿等痛点。于是，一个简单而坚定的想法诞生了——
                      打造一个真正免费、流畅、高清的在线视频平台。
                    </p>
                    <p>
                      从最初的几百部视频资源起步，到如今拥有超过10万部电视剧、电影、动漫、综艺、纪录片等海量内容，
                      棒棒TV用了四年时间，服务了超过5000万用户。我们坚持"不卡顿、免缓冲、流畅播放"的产品理念，
                      不断优化技术架构和用户体验。
                    </p>
                    <p>
                      我们的技术团队分布在北京、上海、深圳三地，致力于CDN加速、视频编解码、
                      智能推荐算法等核心技术的研发，确保每一位用户都能获得极致的观影体验。
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-background-100 border border-background-300/30 rounded-xl p-5 text-center">
                    <div className="text-3xl font-heading font-bold text-primary-400 mb-1">2020</div>
                    <div className="text-xs text-foreground-500">品牌创立</div>
                  </div>
                  <div className="bg-background-100 border border-background-300/30 rounded-xl p-5 text-center">
                    <div className="text-3xl font-heading font-bold text-accent-400 mb-1">10万+</div>
                    <div className="text-xs text-foreground-500">视频资源</div>
                  </div>
                  <div className="bg-background-100 border border-background-300/30 rounded-xl p-5 text-center">
                    <div className="text-3xl font-heading font-bold text-accent-400 mb-1">5000万+</div>
                    <div className="text-xs text-foreground-500">累计用户</div>
                  </div>
                  <div className="bg-background-100 border border-background-300/30 rounded-xl p-5 text-center">
                    <div className="text-3xl font-heading font-bold text-primary-400 mb-1">7×24h</div>
                    <div className="text-xs text-foreground-500">在线服务</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-14">
              <h2 className="text-xl font-heading font-semibold text-foreground-50 mb-6">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    content: '棒棒TV真的太好用了！画质清晰，播放流畅，关键是完全免费。追剧党的福音，已经推荐给身边所有朋友了。',
                    author: '追剧达人小林',
                    rating: 5,
                  },
                  {
                    content: '资源更新速度非常快，热门剧集基本同步更新。分类也做得很清晰，找片子很方便。界面简洁没有乱七八糟的广告。',
                    author: '影视爱好者阿明',
                    rating: 5,
                  },
                  {
                    content: '用棒棒TV两年了，从手机到电脑体验都很好。不卡顿是真的不卡顿，比其他平台流畅太多了。希望越来越棒！',
                    author: '忠实用户小王',
                    rating: 5,
                  },
                  {
                    content: '作为一名宝妈，棒棒TV的少儿频道是我家宝贝的最爱。内容健康、画质好，关键是免费的，太良心了。',
                    author: '宝妈用户李姐',
                    rating: 5,
                  },
                  {
                    content: '晚上回家最享受的就是打开棒棒TV看一部好电影。海量资源随便挑，4K画质看着就是舒服。满分推荐！',
                    author: '电影迷老张',
                    rating: 4,
                  },
                  {
                    content: '从学生时代就开始用棒棒TV，现在工作了还在用。见证了平台越来越好，资源越来越多。强烈安利！',
                    author: '老用户大刘',
                    rating: 5,
                  },
                ].map((review, index) => (
                  <div
                    key={index}
                    className="bg-background-100 border border-background-300/30 rounded-xl p-5"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-400' : 'ri-star-line text-foreground-600'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-300 leading-relaxed mb-4">
                      "{review.content}"
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary-400">
                          {review.author.charAt(0)}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400">{review.author}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact + FAQ */}
            <section className="mb-14">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Contact Form */}
                <div>
                  <h2 className="text-xl font-heading font-semibold text-foreground-50 mb-6">联系我们</h2>
                  <ContactForm />
                </div>

                {/* Contact Info & FAQ */}
                <div>
                  <h2 className="text-xl font-heading font-semibold text-foreground-50 mb-6">常见问题</h2>
                  <div className="space-y-3 mb-8">
                    {faqItems.map((item, index) => (
                      <details key={index} className="group bg-background-100 border border-background-300/30 rounded-lg overflow-hidden">
                        <summary className="px-5 py-3.5 text-sm font-medium text-foreground-200 cursor-pointer hover:text-foreground-100 transition-colors flex items-center justify-between list-none">
                          {item.q}
                          <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200"></i>
                        </summary>
                        <div className="px-5 pb-4 text-sm text-foreground-400 leading-relaxed">
                          {item.a}
                        </div>
                      </details>
                    ))}
                  </div>

                  {/* Contact Info */}
                  <div className="bg-background-100 border border-background-300/30 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-foreground-100 mb-4">联系方式</h3>
                    <div className="space-y-4 text-sm text-foreground-400">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                          <i className="ri-mail-line"></i>
                        </div>
                        <div>
                          <p className="text-foreground-200 font-medium">电子邮箱</p>
                          <p>contact@bangbangtv.com</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                          <i className="ri-customer-service-line"></i>
                        </div>
                        <div>
                          <p className="text-foreground-200 font-medium">客服支持</p>
                          <p>在线客服 7×24小时</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <div>
                          <p className="text-foreground-200 font-medium">公司地址</p>
                          <p>中国北京市朝阳区</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                          <i className="ri-time-line"></i>
                        </div>
                        <div>
                          <p className="text-foreground-200 font-medium">工作时间</p>
                          <p>周一至周日 全天候服务</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Values */}
            <section className="mb-14">
              <h2 className="text-xl font-heading font-semibold text-foreground-50 mb-6 text-center">我们的价值观</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  { icon: 'ri-heart-line', title: '用户至上', desc: '一切以用户体验为核心' },
                  { icon: 'ri-shield-star-line', title: '品质为先', desc: '高清画质，极致流畅' },
                  { icon: 'ri-open-arm-line', title: '开放共享', desc: '免费内容，人人可享' },
                  { icon: 'ri-rocket-line', title: '持续创新', desc: '技术驱动，不断进化' },
                ].map((value, index) => (
                  <div key={index} className="bg-background-100 border border-background-300/30 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 text-primary-400 mb-4">
                      <i className={`${value.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-100 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-400">{value.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}